import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormApiService } from '../../core/services/formapiService';
import { LibraryModule } from '../../shared/lib/lib.module';

type Step = 'email' | 'otp';

@Component({
  selector: 'app-participant-login-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LibraryModule,
  ],
  templateUrl: './participant-login-modal.component.html',
})
export class ParticipantLoginModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() initialEmail: string = '';
  @Output() closed = new EventEmitter<void>();
  @Output() loggedIn = new EventEmitter<{ token: string; userId?: string | number; email: string }>();

  step: Step = 'email';

  email: string = '';
  otp: string = '';
  emailError: string = '';
  otpError: string = '';

  isOtpLoading = false;
  isLoginLoading = false;
  disableGetOtp = false;
  showResend = false;
  timer = 30;
  private timerInterval?: number;

  tempToken: string = '';

  showToaster = false;
  toasterMessage = '';
  toasterType: 'success' | 'error' | 'info' = 'info';

  private readonly destroy$ = new Subject<void>();
  private lastOtpRequestKey = '';
  private lastOtpRequestAt = 0;

  constructor(private readonly api: FormApiService, private readonly router: Router) {}

  ngOnInit(): void {
    // Keep modal state independent; reset when opened by parent (handled in ngOnChanges-like pattern via setter)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.openWithEmail(this.initialEmail);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTimer();
  }

  onModalClose(): void {
    this.resetState();
    this.closed.emit();
  }

  openWithEmail(email: string): void {
    this.email = (email || '').trim().toLowerCase();
    this.step = this.email ? 'otp' : 'email';
    this.otp = '';
    this.emailError = '';
    this.otpError = '';
    this.tempToken = '';
    this.disableGetOtp = false;
    this.showResend = false;
    this.timer = 30;
    this.clearTimer();
    if (this.email) {
      setTimeout(() => this.requestOtp(), 0);
    }
  }

  editEmail(): void {
    this.step = 'email';
    this.otp = '';
    this.otpError = '';
    this.emailError = '';
    this.tempToken = '';
    this.disableGetOtp = false;
    this.showResend = false;
    this.timer = 30;
    this.clearTimer();
  }

  closeToaster(): void {
    this.showToaster = false;
  }

  private toast(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    this.toasterMessage = message;
    this.toasterType = type;
    this.showToaster = true;
  }

  private validateEmail(email: string): boolean {
    const trimmed = (email || '').trim();
    if (!trimmed) return false;
    if (trimmed.length > 254) return false;
    const at = trimmed.indexOf('@');
    if (at <= 0 || at >= trimmed.length - 1) return false;
    const local = trimmed.substring(0, at);
    const domain = trimmed.substring(at + 1);
    if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local) || local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
      return false;
    }
    if (!domain.includes('.') || domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
    if (!/^[a-zA-Z0-9.-]+$/.test(domain)) return false;
    const lastDot = domain.lastIndexOf('.');
    if (lastDot < 0 || domain.length - lastDot - 1 < 2) return false;
    return true;
  }

  requestOtp(): void {
    if (this.isOtpLoading || this.disableGetOtp) {
      return;
    }

    this.emailError = '';
    this.otpError = '';
    const normalizedEmail = (this.email || '').trim().toLowerCase();

    if (!normalizedEmail) {
      this.emailError = 'Please enter your email';
      return;
    }
    if (!this.validateEmail(normalizedEmail)) {
      this.emailError = 'Please enter a valid email address';
      return;
    }

    // Prevent accidental duplicate /login calls from rapid re-renders/clicks.
    const requestKey = `otp:${normalizedEmail}`;
    const now = Date.now();
    if (this.lastOtpRequestKey === requestKey && now - this.lastOtpRequestAt < 1200) {
      return;
    }
    this.lastOtpRequestKey = requestKey;
    this.lastOtpRequestAt = now;

    this.isOtpLoading = true;
    this.disableGetOtp = true;
    this.showResend = false;
    this.clearTimer();

    this.api
      .login({ email: normalizedEmail })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isOtpLoading = false;
          // webapp behavior: response.tempToken + response.message
          const temp = response?.tempToken ?? response?.data?.tempToken ?? '';
          if (!temp) {
            this.disableGetOtp = false;
            this.emailError = response?.message || 'Unable to send OTP. Please try again.';
            return;
          }

          this.tempToken = String(temp);
          this.step = 'otp';
          this.toast(response?.message || 'OTP has been sent. Please check your email.', 'success');
          this.startTimer();
        },
        error: (error: any) => {
          this.isOtpLoading = false;
          this.disableGetOtp = false;
          this.emailError = error?.error?.message || 'Unable to send OTP. Please try again.';
        },
      });
  }

  onOtpChanged(value: string): void {
    this.otp = value || '';
    if (this.otpError && this.otp.length !== 6) {
      this.otpError = '';
    }
  }

  submitLogin(): void {
    if (this.isLoginLoading) {
      return;
    }

    this.emailError = '';
    this.otpError = '';

    const normalizedEmail = (this.email || '').trim().toLowerCase();
    if (!this.tempToken) {
      this.otpError = 'Sending OTP. Please wait and try again.';
      return;
    }

    if (!this.otp || this.otp.trim().length !== 6) {
      this.otpError = 'Please enter the 6-digit OTP.';
      return;
    }

    this.isLoginLoading = true;
    this.api
      .verifyOtp({ otp: this.otp.trim() }, this.tempToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoginLoading = false;
          const token = response?.token ?? response?.data?.token ?? '';
          const userId = response?.id ?? response?.data?.id;

          if (!token) {
            this.otpError = 'Login failed. Please try again.';
            return;
          }

          sessionStorage.setItem('participant_auth_token', String(token));
          sessionStorage.setItem('participant_email', normalizedEmail);
          if (userId != null) {
            localStorage.setItem('user_id', String(userId));
          }

          this.loggedIn.emit({ token, userId, email: normalizedEmail });
          this.toast("You're logged in!", 'success');

          // Close modal and navigate to Senior Center program enrollment
          this.resetState();
          this.closed.emit();
          this.router.navigate(['/program/1000']);
        },
        error: () => {
          this.isLoginLoading = false;
          this.otpError = 'Invalid OTP. Please try again.';
        },
      });
  }

  onOtpEnterKey(event: Event): void {
    (event as KeyboardEvent).preventDefault();
    if (!this.isLoginLoading && !this.isOtpLoading && this.tempToken) {
      this.submitLogin();
    }
  }

  onEmailEnterKey(event: Event): void {
    (event as KeyboardEvent).preventDefault();
    if (!this.isOtpLoading && !this.disableGetOtp && this.step === 'email') {
      this.requestOtp();
    }
  }

  resendOtp(): void {
    if (!this.showResend) return;
    this.otp = '';
    this.otpError = '';
    this.requestOtp();
  }

  private startTimer(): void {
    this.timer = 30;
    this.showResend = false;
    this.clearTimer();
    this.timerInterval = window.setInterval(() => {
      this.timer--;
      if (this.timer <= 0) {
        this.clearTimer();
        this.showResend = true;
        this.disableGetOtp = false;
      }
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
  }

  private resetState(): void {
    this.step = 'email';
    this.email = '';
    this.otp = '';
    this.emailError = '';
    this.otpError = '';
    this.isOtpLoading = false;
    this.isLoginLoading = false;
    this.disableGetOtp = false;
    this.showResend = false;
    this.timer = 30;
    this.tempToken = '';
    this.clearTimer();
    this.showToaster = false;
    this.toasterMessage = '';
  }
}

